import React, { useState } from 'react';
import { Box, Button, Grid, Stack, TextField, Typography, alpha } from '@mui/material';
import axiosPublic from '../../../../api/axiosPublic';
import { getRootSubdomain } from '../../../utils/subdomain';
import { getAccentColor } from './shared';

const InquiryFormSection = ({ section, sectionKey, showSnackbar, bodyContent }) => {
  const accent = getAccentColor(section) || '#4F46E5';
  const isAdmissions = section.type === 'admissions_form';
  const tenantKey = getRootSubdomain();
  const [submitting, setSubmitting] = useState(false);

  const fields = isAdmissions
    ? [
        { name: 'parentName', label: 'Parent Name', type: 'text', required: true },
        { name: 'phone', label: 'Phone Number', type: 'tel', required: true },
        { name: 'studentName', label: 'Student Name', type: 'text', required: false },
        { name: 'grade', label: 'Class Applying For', type: 'text', required: false },
        { name: 'email', label: 'Email Address (Optional)', type: 'email', required: false },
      ]
    : [
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'phone', label: 'Phone Number', type: 'tel', required: true },
        { name: 'email', label: 'Email Address (Optional)', type: 'email', required: false },
      ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!tenantKey) {
      showSnackbar('Website context is missing.', 'error');
      return;
    }

    const formData = new FormData(event.currentTarget);
    const payload = {
      requestType: isAdmissions ? 'ADMISSION' : 'CONTACT',
      name: String(formData.get('name') || formData.get('parentName') || '').trim(),
      studentName: String(formData.get('studentName') || '').trim(),
      parentName: String(formData.get('parentName') || '').trim(),
      grade: String(formData.get('grade') || '').trim(),
      phone: String(formData.get('phone') || '').trim(),
      email: String(formData.get('email') || '').trim(),
      message: String(formData.get('message') || '').trim(),
      sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    };

    setSubmitting(true);
    try {
      await axiosPublic.post(`/api/${tenantKey}/website-requests`, payload, {
        skipGlobalLoader: true,
      });
      showSnackbar(section.formSuccessMessage || 'Form submitted successfully.', 'success');
      event.currentTarget.reset();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Failed to submit form.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="section" sx={{ py: { xs: 1, md: 1.6 }, width: '100%' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, borderRadius: '32px', overflow: 'hidden', boxShadow: `0 20px 40px ${alpha('#0F172A', 0.08)}`, border: `1px solid ${alpha('#0F172A', 0.05)}` }}>
        
        {/* Left Side: Dark Modern Brand Area */}
        <Box sx={{ position: 'relative', p: { xs: 4, md: 6 }, backgroundColor: '#0F172A', color: '#FFFFFF', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ position: 'absolute', top: '-20%', left: '-20%', width: '100%', height: '100%', background: `radial-gradient(circle, ${alpha(accent, 0.4)} 0%, transparent 70%)`, filter: 'blur(40px)', zIndex: 0 }} />
          <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
            {section.title && <Typography sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '2.5rem' }, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{section.title}</Typography>}
            {bodyContent && <Box sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.05rem', lineHeight: 1.6 }}>{bodyContent}</Box>}
            <Box sx={{ mt: 2, p: 2.5, borderRadius: '6px', backgroundColor: alpha('#FFFFFF', 0.05), border: `1px solid ${alpha('#FFFFFF', 0.1)}`, backdropFilter: 'blur(10px)' }}>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.9rem', lineHeight: 1.6, fontWeight: 500 }}>{section.formSuccessMessage || 'We typically respond within 24 hours. We look forward to connecting with you.'}</Typography>
            </Box>
          </Stack>
        </Box>

        {/* Right Side: Clean Form */}
        <Box sx={{ p: { xs: 4, md: 6 }, backgroundColor: '#FFFFFF' }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2.5}>
              {fields.map((field) => (
                <Grid item xs={12} md={field.name === 'message' || fields.length % 2 !== 0 && field === fields[fields.length-1] ? 12 : 6} key={`${sectionKey}-${field.name}`}>
                  <TextField fullWidth required={Boolean(field.required)} label={field.label} type={field.type} name={field.name} variant="outlined" InputProps={{ sx: { borderRadius: '6px', backgroundColor: '#F8FAFC' } }} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={4} label="Message (Optional)" name="message" variant="outlined" InputProps={{ sx: { borderRadius: '6px', backgroundColor: '#F8FAFC' } }} />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" disabled={submitting} variant="contained" fullWidth sx={{ mt: 1, py: 1.8, borderRadius: '6px', backgroundColor: accent, color: '#FFFFFF', fontWeight: 800, fontSize: '1.05rem', textTransform: 'none', boxShadow: `0 8px 20px ${alpha(accent, 0.3)}`, '&:hover': { backgroundColor: accent, transform: 'translateY(-2px)', boxShadow: `0 12px 24px ${alpha(accent, 0.4)}` }, transition: 'all 0.3s ease' }}>
                  {submitting ? 'Submitting...' : (section.formSubmitLabel || 'Submit Inquiry')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default InquiryFormSection;
